import axios from "axios";

// const API = axios.create({ baseURL: '/api' });

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

// Attach JWT token if available
API.interceptors.request.use((config) => {
  const token = localStorage.getItem("skillyer_user_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

API.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || "";
    const isAuthApi =
      url.includes("/users/login") ||
      url.includes("/users/register") ||
      url.includes("/admin/login");

    if (status === 401 && !isAuthApi) {
      localStorage.removeItem("skillyer_user_token");
      localStorage.removeItem("skillyer_user");
    }

    return Promise.reject(error);
  },
);

// Courses
export const fetchCourses = (params) => API.get("/courses", { params });
export const fetchCourse = (id) => API.get(`/courses/${id}`);
export const createCourse = (data) => API.post("/courses", data);
export const updateCourse = (id, data) => API.put(`/courses/${id}`, data);
export const deleteCourse = (id) => API.delete(`/courses/${id}`);

// Categories
export const fetchCategories = () => API.get("/categories");
export const createCategory = (data) => API.post("/categories", data);
export const updateCategory = (id, data) => API.put(`/categories/${id}`, data);
export const deleteCategory = (id) => API.delete(`/categories/${id}`);

// Providers
export const fetchProviders = () => API.get("/providers");
export const createProvider = (data) =>
  API.post("/providers", data, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
export const updateProvider = (id, data) =>
  API.put(`/providers/${id}`, data, {
    headers:
      data instanceof FormData ? { "Content-Type": "multipart/form-data" } : {},
  });
export const deleteProvider = (id) => API.delete(`/providers/${id}`);

// Jobs
export const fetchJobs = (params) => API.get("/jobs", { params });
export const createJob = (data) => API.post("/jobs", data);
export const updateJob = (id, data) => API.put(`/jobs/${id}`, data);
export const deleteJob = (id) => API.delete(`/jobs/${id}`);

// Enrollments
export const fetchEnrollments = (params) => API.get("/enrollments", { params });
export const createEnrollment = (data) => API.post("/enrollments", data);
export const updateEnrollmentStatus = (id, status) =>
  API.put(`/enrollments/${id}/status`, { status });
export const updateEnrollment = (id, data) =>
  API.put(`/enrollments/${id}`, data);
export const deleteEnrollment = (id) => API.delete(`/enrollments/${id}`);

export const downloadEnrollmentsExcel = () =>
  API.get("/enrollments/export-excel", {
    responseType: "blob",
  });

export const downloadCoursesExcel = () =>
  API.get("/courses/export-excel", {
    responseType: "blob",
  });

// Applications — uses FormData for file upload
export const fetchApplications = (params) =>
  API.get("/applications", { params });
export const createApplication = (formData) =>
  API.post("/applications", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
export const updateApplicationStatus = (id, status) =>
  API.put(`/applications/${id}/status`, { status });
export const deleteApplication = (id) => API.delete(`/applications/${id}`);

export const downloadApplicationsExcel = () =>
  API.get("/applications/export-excel", {
    responseType: "blob", // 👈 important for file
  });

// Counsel
export const fetchCounselRequests = (params) => API.get("/counsel", { params });
export const createCounselRequest = (data) => API.post("/counsel", data);
export const updateCounselStatus = (id, status) =>
  API.put(`/counsel/${id}/status`, { status });
export const downloadCounselExcel = () =>
  API.get("/counsel/export-excel", {
    responseType: "blob",
  });

// Testimonials
export const fetchTestimonials = () => API.get("/testimonials");
export const fetchTestimonialsAdmin = () => API.get("/testimonials/admin/all");
export const createTestimonial = (data) => API.post("/testimonials", data);
export const updateTestimonial = (id, data) =>
  API.put(`/testimonials/${id}`, data);
export const deleteTestimonial = (id) => API.delete(`/testimonials/${id}`);

// Blogs
export const fetchBlogs = () => API.get("/blogs");
export const fetchBlogBySlug = (slug) => API.get(`/blogs/slug/${slug}`);
export const fetchBlogsAdmin = () => API.get("/blogs/admin/all");
export const fetchBlogAdmin = (id) => API.get(`/blogs/admin/${id}`);
export const createBlog = (formData) =>
  API.post("/blogs", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
export const updateBlog = (id, formData) =>
  API.put(`/blogs/${id}`, formData, {
    headers:
      formData instanceof FormData
        ? { "Content-Type": "multipart/form-data" }
        : {},
  });
export const deleteBlog = (id) => API.delete(`/blogs/${id}`);
export const uploadBlogContentImage = (formData) =>
  API.post("/blogs/upload-image", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

// ROI
export const fetchRoiData = () => API.get("/roi");

// Admin
export const adminLogin = (data) => API.post("/admin/login", data);
export const fetchAdminStats = () => API.get("/admin/stats");
export const fetchRecentActivity = () => API.get("/admin/recent-activity");

// Users (Auth)
export const registerUser = (data) => API.post("/users/register", data);
export const loginUser = (data) => API.post("/users/login", data);
export const fetchUserProfile = () => API.get("/users/profile");
export const fetchAllUsers = (params) => API.get("/users/all", { params });
export const toggleUserStatus = (id) => API.patch(`/users/${id}/toggle-status`);
export const deleteUser = (id) => API.delete(`/users/${id}`);

export default API;
